import React, { useEffect, useState } from "react";
import { getAchievements } from "../lib/api";
import { useProfile } from "../context/ProfileContext";
import * as Icons from "lucide-react";
import { Lock } from "lucide-react";

function check(criterion, p) {
  // very small DSL: comma/AND-joined comparisons "key OP num"
  const parts = criterion.split(/\s+AND\s+|\s*,\s*/i);
  return parts.every((expr) => {
    const m = expr.match(/(\w+)\s*(>=|<=|>|<|=)\s*(\d+)/);
    if (!m) return false;
    const [, key, op, num] = m;
    const val = p[key] || 0;
    const n = parseInt(num);
    return op === ">=" ? val >= n
      : op === "<=" ? val <= n
      : op === ">" ? val > n
      : op === "<" ? val < n
      : val === n;
  });
}

export default function Achievements({ compact = false }) {
  const { profile } = useProfile();
  const [data, setData] = useState(null);

  useEffect(() => { getAchievements().then(setData); }, []);

  if (!data) return null;
  const items = data.badges.map((b) => ({ ...b, unlocked: check(b.criterion, profile) }));
  const unlockedCount = items.filter((i) => i.unlocked).length;

  return (
    <section className={compact ? "" : "max-w-7xl mx-auto px-5 md:px-10 py-8 md:py-12"} data-testid="achievements-section">
      {!compact && (
        <div className="mb-7">
          <div className="text-[11px] tracking-[0.22em] uppercase text-[#3A5A40] font-semibold">Achievements</div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">{unlockedCount} / {items.length} unlocked</h2>
          <p className="text-[#4A4A4A] mt-2">Keep learning to collect them all.</p>
        </div>
      )}
      {compact && (
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#3A5A40] font-semibold">Achievements</div>
            <div className="font-heading text-xl font-bold">{unlockedCount} / {items.length} unlocked</div>
          </div>
        </div>
      )}
      <div className={`grid gap-4 ${compact ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"}`}>
        {items.map((b, i) => {
          const Icon = Icons[b.icon] || Icons.Star;
          return (
            <div
              key={b.id}
              className={`card p-5 transition ${b.unlocked ? "" : "opacity-60"}`}
              data-testid={`badge-${b.id}`}
            >
              <div className={`w-11 h-11 rounded-xl grid place-items-center mb-3 ${
                b.unlocked ? "bg-[#FBE7DF] text-[#E07A5F]" : "bg-[#EBE7E0] text-[#4A4A4A]"
              }`}>
                {b.unlocked ? <Icon size={20} /> : <Lock size={18} />}
              </div>
              <div className="font-heading font-semibold text-[#1A1A1A]" data-testid={`badge-${b.id}-title`}>{b.title}</div>
              <div className="text-xs text-[#4A4A4A] mt-1 leading-relaxed">{b.description}</div>
              {b.unlocked && (
                <span className="inline-block mt-3 text-[10px] tracking-[0.18em] uppercase font-bold text-[#3A5A40] bg-[#EAECE6] px-2 py-1 rounded-full" data-testid={`badge-${b.id}-status`}>
                  Unlocked
                </span>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
